/**
 * ReportExportMenu — reusable dropdown menu for exporting + sharing.
 *
 * Slots into any report card. Caller passes:
 *   - title (used in filenames + subject lines)
 *   - subtitle (e.g. date range)
 *   - getCsvData (returns headers + rows for CSV)
 *   - getPdfSections (returns PDF section structs)
 *   - getShareSummary (returns plain-text summary for email/WhatsApp body)
 *
 * The menu shows:
 *   - Download as PDF
 *   - Download as CSV
 *   - Send via Email      (also triggers PDF download)
 *   - Share on WhatsApp   (also triggers PDF download)
 *   - Copy summary
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Download,
  FileText,
  FileSpreadsheet,
  Mail,
  MessageCircle,
  Copy,
  Check,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { exportPdf, exportCsv, type PdfSection } from "@/utils/reportExport";
import {
  shareViaEmail,
  shareViaWhatsApp,
  copyToClipboard,
} from "@/utils/reportShare";

interface CsvData {
  headers: string[];
  rows: Array<Array<string | number | null | undefined>>;
}

interface Props {
  title: string;
  subtitle?: string;
  /** Lazy: only called when user clicks PDF or one of the share options. */
  getCsvData: () => CsvData;
  getPdfSections: () => PdfSection[];
  /** Plain-text summary used for Email body, WhatsApp text, Copy link. */
  getShareSummary: () => string;
  /** Optional: disable the menu (e.g. when data still loading). */
  disabled?: boolean;
}

const ReportExportMenu: React.FC<Props> = ({
  title,
  subtitle,
  getCsvData,
  getPdfSections,
  getShareSummary,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const meta = `Generated ${new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })}`;

  const handlePdf = async () => {
    setWorking("pdf");
    try {
      exportPdf({
        title,
        subtitle,
        meta,
        sections: getPdfSections(),
      });
    } finally {
      setTimeout(() => setWorking(null), 600);
      setOpen(false);
    }
  };

  const handleCsv = async () => {
    setWorking("csv");
    try {
      const { headers, rows } = getCsvData();
      exportCsv({ title, subtitle, headers, rows });
    } finally {
      setTimeout(() => setWorking(null), 600);
      setOpen(false);
    }
  };

  const handleEmail = async () => {
    setWorking("email");
    try {
      // Trigger PDF download FIRST so it's in the user's downloads when
      // their mail client opens.
      exportPdf({ title, subtitle, meta, sections: getPdfSections() });
      // Small delay so the download starts before the mailto: navigation
      setTimeout(() => {
        shareViaEmail({
          subject: `${title}${subtitle ? ` — ${subtitle}` : ""}`,
          body:
            getShareSummary() +
            "\n\n(The full PDF was downloaded — attach it from your downloads folder.)",
        });
      }, 200);
    } finally {
      setTimeout(() => setWorking(null), 600);
      setOpen(false);
    }
  };

  const handleWhatsApp = async () => {
    setWorking("whatsapp");
    try {
      exportPdf({ title, subtitle, meta, sections: getPdfSections() });
      setTimeout(() => {
        shareViaWhatsApp({
          text:
            `*${title}*${subtitle ? `\n${subtitle}` : ""}\n\n` +
            getShareSummary() +
            "\n\n(PDF downloaded — attach from your downloads.)",
        });
      }, 200);
    } finally {
      setTimeout(() => setWorking(null), 600);
      setOpen(false);
    }
  };

  const handleCopy = async () => {
    setWorking("copy");
    try {
      await copyToClipboard(
        `${title}${subtitle ? ` — ${subtitle}` : ""}\n\n${getShareSummary()}`,
      );
    } finally {
      setTimeout(() => setWorking(null), 600);
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className={[
          "inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]",
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-muted/40 hover:border-foreground/20",
        ].join(" ")}
        aria-label="Export or share report"
        aria-expanded={open}
      >
        <Download className="h-3.5 w-3.5" />
        Export & share
        <ChevronDown
          className={`h-3 w-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1.5 w-60 rounded-lg border border-border bg-card shadow-lg overflow-hidden"
        >
          <MenuRow
            icon={<FileText className="h-3.5 w-3.5" />}
            label="Download as PDF"
            hint="Polished, branded"
            onClick={handlePdf}
            working={working === "pdf"}
          />
          <MenuRow
            icon={<FileSpreadsheet className="h-3.5 w-3.5" />}
            label="Download as CSV"
            hint="For Excel / Sheets"
            onClick={handleCsv}
            working={working === "csv"}
          />
          <div className="border-t border-border" />
          <MenuRow
            icon={<Mail className="h-3.5 w-3.5" />}
            label="Send via Email"
            hint="Downloads PDF + opens email"
            onClick={handleEmail}
            working={working === "email"}
          />
          <MenuRow
            icon={<MessageCircle className="h-3.5 w-3.5" />}
            label="Share on WhatsApp"
            hint="Downloads PDF + opens WhatsApp"
            onClick={handleWhatsApp}
            working={working === "whatsapp"}
          />
          <div className="border-t border-border" />
          <MenuRow
            icon={
              working === "copy" ? (
                <Check className="h-3.5 w-3.5 text-[#4CAF50]" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )
            }
            label="Copy summary text"
            hint="To paste anywhere"
            onClick={handleCopy}
            working={working === "copy"}
          />
        </div>
      )}
    </div>
  );
};

const MenuRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
  working: boolean;
}> = ({ icon, label, hint, onClick, working }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={working}
    className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-muted/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    role="menuitem"
  >
    <div className="mt-0.5 text-muted-foreground shrink-0">
      {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium leading-tight">{label}</div>
      <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
        {hint}
      </div>
    </div>
  </button>
);

export default ReportExportMenu;
